import React, { useState, useContext } from "react";
import AppContext from "../../../../context/AppContext";
import { AreaChart, BarChart3, PieChart, Radar } from "lucide-react";
import translations from "../../../common/Translations";
import ModalCloseButton from "../../../common/ModalCloseButton";

/*
 * The four-chart picker every dashboard card opens from its eye icon.
 *
 * `options` opts a caller out of that fixed list: pass your own choices and the
 * modal becomes a generic view picker, keeping the same chrome, grid and hover
 * states so a card offering "bar / share / table" looks like every other card's
 * picker rather than a bespoke dialog. Callers that don't pass it are unchanged
 * -- they still drive the global `setView` as before.
 *
 * options: [{ key, name, icon, value, description? }]
 * value/onSelect make it controlled; without `options` selection stays internal.
 */
export default function ChartModal({
  setCurrentPage,
  showModal,
  setShowModal,
  setChartView,
  options,
  value,
  onSelect,
  title,
}) {
  const context = useContext(AppContext);
  const [selectedChart, setSelectedChart] = useState(null);
  const { setView, theme, langMode } = context;

  const modalTitle = title ?? translations[langMode]?.SelectChart;

  const chartInfo = {
    PIECHART: {
      name: translations[langMode].Piechart,
      icon: <PieChart className="h-9 w-9 text-yellow-500 transition-colors duration-200" />,
      verify: "chart",
    },
    BARCHART: {
      name: translations[langMode].barChart,
      icon: <BarChart3 className="h-9 w-9 text-blue-600 transition-colors duration-200" />,
      verify: "graph",
    },
    AREACHART: {
      name: translations[langMode].Areachart,
      icon: <AreaChart className="h-9 w-9 text-green-500 transition-colors duration-200" />,
      verify: "area",
    },
    RADARCHART: {
      name: translations[langMode].Radarchart,
      icon: <Radar className="h-9 w-9 text-purple-600 transition-colors duration-200" />,
      verify: "radar",
    },
  };

  const handleChangeView = (verify) => {
    setView(verify);
    setChartView(verify);
    setShowModal(false);
    setSelectedChart(verify);
    if (typeof setCurrentPage === "function") {
      setCurrentPage(0);
    }
  };

  /* Both modes render from one list, so the custom picker can never drift from
     the built-in one's markup. */
  const items =
    options ??
    Object.entries(chartInfo).map(([key, entry]) => ({
      key,
      name: entry.name,
      icon: entry.icon,
      value: entry.verify,
    }));

  // Controlled when the caller supplies options; internal state otherwise.
  const activeValue = options ? value : selectedChart;

  const handlePick = (picked) => {
    if (options) {
      if (typeof onSelect === "function") onSelect(picked);
      setShowModal(false);
      return;
    }
    handleChangeView(picked);
  };

  if (!showModal) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4">
      <div
        className={`relative w-full max-w-xl rounded-2xl shadow-2xl ${
          theme === "dark" ? "border border-gray-700 bg-gray-800 text-white" : ""
        } ${theme === "high-contrast" ? "border border-white bg-black text-white" : "bg-white"}`}
        dir={langMode === "ar" ? "rtl" : "ltr"}
      >
        <div
          className={`flex items-center justify-between border-b px-5 py-4 ${
            theme === "dark" ? "border-gray-700" : ""
          } ${theme === "high-contrast" ? "border-white" : "border-gray-200"}`}
        >
          <h3
            className={`text-lg font-bold sm:text-xl ${
              theme === "dark" ? "text-white" : theme === "high-contrast" ? "text-white" : "text-gray-800"
            }`}
          >
            {modalTitle}
          </h3>
          <ModalCloseButton
            onClick={() => setShowModal(false)}
            className={langMode === "ar" ? "mr-auto ml-0" : "ml-auto mr-0"}
          />
        </div>

        <div className="p-5 sm:p-6">
          <div className="grid grid-cols-2 gap-4 sm:gap-5">
            {items.map(({ key, name, icon, value: itemValue, description }) => (
              <button
                key={key}
                className={`group flex flex-col items-center justify-center rounded-xl border p-5 transition-all duration-200 ${
                  activeValue === itemValue
                    ? "border-blue-500 bg-blue-50 text-blue-700"
                    : `border-gray-200 hover:border-blue-300 hover:bg-blue-50/40
                    ${theme === "dark" ? "border-gray-600 bg-gray-700 hover:bg-gray-600 text-gray-200" : ""}
                    ${theme === "high-contrast" ? "border-white bg-gray-900 hover:bg-gray-800 text-white" : "bg-white text-gray-700"}`
                }`}
                onClick={() => handlePick(itemValue)}
                aria-pressed={activeValue === itemValue}
              >
                <div className="mb-3">
                  {React.cloneElement(icon, {
                    className: `${icon.props.className} ${activeValue === itemValue ? "text-blue-600" : ""}`,
                  })}
                </div>
                <span
                  className={`text-center text-sm font-semibold sm:text-lg ${
                    activeValue === itemValue
                      ? "text-blue-700"
                      : `${theme === "dark" ? "text-gray-200" : theme === "high-contrast" ? "text-white" : "text-gray-800"}`
                  }`}
                >
                  {name}
                </span>
                {/* Optional one-liner: "Table" alone doesn't say what the table
                    gives you that the bars don't. */}
                {description && (
                  <span
                    className={`mt-1 text-center text-[11px] leading-snug ${
                      theme === "dark" || theme === "high-contrast"
                        ? "text-gray-400"
                        : "text-gray-500"
                    }`}
                  >
                    {description}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
